import { createContext, useContext, useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../config";
import { translations } from "../translations";

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) return { language: "en", setLanguage: () => {}, t: translations.en };
  return ctx;
};

const detectInitialLanguage = () => {
  try {
    // URL override wins: ?lang=sk — useful for deep-link localization
    // and for the screenshot/testing tool which cannot set localStorage
    // reliably before the page loads.
    const urlLang = new URLSearchParams(window.location.search).get("lang");
    if (urlLang && translations[urlLang]) {
      try { localStorage.setItem("trp_lang", urlLang); } catch {}
      return { lang: urlLang, fromStorage: true };
    }
    const saved = localStorage.getItem("trp_lang");
    if (saved && translations[saved]) return { lang: saved, fromStorage: true };
  } catch {}
  // Check browser language as immediate fallback (no async needed)
  const bl = navigator.language?.split("-")[0];
  if (bl && translations[bl]) return { lang: bl, fromStorage: false };
  return { lang: "en", fromStorage: false };
};

const RTL_LANGUAGES = new Set(["ar", "he", "fa", "ur"]);

export const LanguageProvider = ({ children }) => {
  const initial = detectInitialLanguage();
  const [language, setLanguage] = useState(initial.lang);
  const detectedFromStorage = useRef(initial.fromStorage);

  // Save to localStorage whenever language changes (after initial detect)
  useEffect(() => {
    try { localStorage.setItem("trp_lang", language); } catch {}
    // Keep <html dir="…" lang="…"> in sync so Tailwind rtl: variants and
    // screen-readers pick up the text direction automatically.
    try {
      const dir = RTL_LANGUAGES.has(language) ? "rtl" : "ltr";
      document.documentElement.setAttribute("dir", dir);
      const p = window.location.pathname;
      const forced = /^\/((gratulujeme|tvorba-webu)\/cz|cz\/)/.test(p) ? "cs" : /^\/((gratulujeme|tvorba-webu)\/sk|rebricek-zlepseni)/.test(p) ? "sk" : null;
      document.documentElement.setAttribute("lang", forced || language);
    } catch {}
  }, [language]);

  // Try server-side geolocation for better accuracy (only if not already saved).
  // Sends navigator.languages as an Accept-Language-style query param so the
  // server can combine IP-country + browser-preferred-language into a
  // smarter guess (e.g. a Slovak living in Germany gets Slovak, not German).
  //
  // Fires "trp:lang-auto-switched" whenever the server confirms a browser-
  // preference signal OR auto-corrects our guess, even if the UI language
  // didn't actually change (e.g. navigator.language already said 'sk'
  // synchronously, and the server confirms ip_language='de'). This lets the
  // arrival banner surface the "Using Slovenčina — Use 🇩🇪 Deutsch instead?"
  // prompt for a Slovak user physically in Germany.
  useEffect(() => {
    if (detectedFromStorage.current) return;
    const detect = async () => {
      try {
        const al = (navigator.languages || [navigator.language]).filter(Boolean);
        const alHeader = al
          .map((lng, i) => (i === 0 ? lng : `${lng};q=${(1 - i * 0.1).toFixed(1)}`))
          .join(",");
        const r = await axios.get(`${API}/detect-location`, {
          params: alHeader ? { accept_language: alHeader } : {},
        });
        const sl = r.data?.suggested_language;
        const reason = r.data?.reason;
        const ipLang = r.data?.ip_language;
        if (!sl || !translations[sl]) return;

        const prev = language;
        const actuallyChanged = sl !== language;
        if (actuallyChanged) setLanguage(sl);

        // Decide whether to notify the banner. We fire the event when:
        //  - We switched the UI language (existing flow), OR
        //  - reason=browser_wins AND the IP-country's language exists + differs
        //    (the banner will offer "Use 🇩🇪 Deutsch" as an alternative).
        const shouldSurfaceBrowserWins =
          reason === "browser_wins" && ipLang && ipLang !== sl && translations[ipLang];
        if (!actuallyChanged && !shouldSurfaceBrowserWins) return;

        try {
          window.dispatchEvent(new CustomEvent("trp:lang-auto-switched", {
            detail: {
              from: prev,
              to: sl,
              country: r.data?.country_code,
              country_name: r.data?.country_name,
              reason,
              ip_language: ipLang,
              browser_language: r.data?.browser_language,
              confidence: r.data?.confidence,
            },
          }));
        } catch {}
      } catch {}
    };
    detect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLang = (lang) => {
    detectedFromStorage.current = true;
    setLanguage(lang);
  };

  const resolvedLang = language === "en-gb" ? "en" : language;
  const t = translations[resolvedLang] || translations.en;

  return (
    <LanguageContext.Provider value={{ language, setLanguage: setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
